import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useState, lazy, Suspense } from "react";

// Componente para scroll ao topo quando muda de página
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Componente de Loading
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
    <div className="glass-panel dark:bg-slate-800/50 rounded-3xl p-8 flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-600 dark:text-slate-300">Carregando...</p>
    </div>
  </div>
);

// Helper para envolver componente lazy com Suspense
const withSuspense = (Component) => (props) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// Páginas críticas (carregadas imediatamente)
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";

// Landing Pages Galpão (Salinet) - ativas em galpao.salinetempreendimentos.com.br
const GalpaoLanding = lazy(() => import("@/pages/GalpaoLanding"));
const GalpaoObrigado = lazy(() => import("@/pages/GalpaoObrigado"));

// Landing Page Zênite by Salinet - será mapeada para zenitebysalinet.com.br
const ZeniteLanding = lazy(() => import("@/pages/ZeniteLanding"));

// Landing de Carreiras - usada em lp.salinetempreendimentos.com.br/curriculo
const CurriculoLanding = lazy(() => import("@/pages/CurriculoLanding"));
const CurriculoObrigado = lazy(() => import("@/pages/CurriculoObrigado"));

// Lazy loading para páginas do CRM
const FunilVendas = lazy(() => import("@/pages/FunilVendas"));
const Oportunidades = lazy(() => import("@/pages/Oportunidades"));
const OportunidadeDetalhes = lazy(() => import("@/pages/OportunidadeDetalhes"));
const Clients = lazy(() => import("@/pages/Clients"));
const Properties = lazy(() => import("@/pages/Properties"));
const Profile = lazy(() => import("@/pages/Profile"));
const ActionLogs = lazy(() => import("@/pages/ActionLogs"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const Captacoes = lazy(() => import("@/pages/Captacoes"));
const Empreendimentos = lazy(() => import("@/pages/Empreendimentos"));
const EmpreendimentoDetalhes = lazy(() => import("@/pages/EmpreendimentoDetalhes"));
const ClienteDetalhes = lazy(() => import("@/pages/ClienteDetalhes"));
const CaptacaoDetalhes = lazy(() => import("@/pages/CaptacaoDetalhes"));
const Construtoras = lazy(() => import("@/pages/Construtoras"));
const Proprietarios = lazy(() => import("@/pages/Proprietarios"));
const Agendamentos = lazy(() => import("@/pages/Agendamentos"));
const Equipe = lazy(() => import("@/pages/Equipe"));
const RBACManager = lazy(() => import("@/pages/RBACManager"));
const Disputas = lazy(() => import("@/pages/Disputas"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const MiniSite = lazy(() => import("@/pages/MiniSite"));
const LandingPages = lazy(() => import("@/pages/LandingPages"));
const LandingPageEditor = lazy(() => import("@/pages/LandingPageEditor"));
const SiteConfig = lazy(() => import("@/pages/SiteConfig"));
const Temas = lazy(() => import("@/pages/Temas"));
const Visitantes = lazy(() => import("@/pages/Visitantes"));
const MetaCAPIConfig = lazy(() => import("@/pages/MetaCAPIConfig"));
const Importacao = lazy(() => import("@/pages/Importacao"));
const Lembretes = lazy(() => import("@/pages/Lembretes"));
const EmailMarketing = lazy(() => import("@/pages/EmailMarketing"));
const Propostas = lazy(() => import("@/pages/Propostas"));
const Atualizacao = lazy(() => import("@/pages/Atualizacao"));
const Tabelas = lazy(() => import("@/pages/Tabelas"));
const UnidadePerfil = lazy(() => import("@/pages/UnidadePerfil"));
const PropostasPage = lazy(() => import("@/pages/Propostas"));
const PropostaPerfil = lazy(() => import("@/pages/PropostaPerfil"));

// Índices Econômicos
const CubHistorico = lazy(() => import("@/pages/CubHistorico"));
const Igpm = lazy(() => import("@/pages/Igpm"));
const Incc = lazy(() => import("@/pages/Incc"));
const Ipca = lazy(() => import("@/pages/Ipca"));

// Bolsão de Leads
const Bolsao = lazy(() => import("@/pages/Bolsao"));
const CampanhasBolsao = lazy(() => import("@/pages/CampanhasBolsao"));
const MinhaCampanha = lazy(() => import("@/pages/MinhaCampanha"));
const CampanhaDetalhes = lazy(() => import("@/pages/CampanhaDetalhes"));

// Pixel Pro - Tracking Comportamental
const PixelProDashboard = lazy(() => import("@/pages/PixelProDashboard"));
const PixelProEvents = lazy(() => import("@/pages/PixelProEvents"));
const PixelProRules = lazy(() => import("@/pages/PixelProRules"));
const PixelProWhatsApp = lazy(() => import("@/pages/PixelProWhatsApp"));

// InstaIMOB ADS - Gestão de Anúncios
const InstaIMOBAdsDashboard = lazy(() => import("@/pages/InstaIMOBAdsDashboard"));
const CampanhasWhatsApp = lazy(() => import("@/pages/CampanhasWhatsApp"));

// QR Codes Inteligentes
const QRCodesListPage = lazy(() => import("@/pages/QRCodesListPage"));
const QRCodeProfilePage = lazy(() => import("@/pages/QRCodeProfilePage"));

// Domínios
const Dominios = lazy(() => import("@/pages/Dominios"));

// Painel de Negociações
const NegociacoesAoVivo = lazy(() => import("@/pages/NegociacoesAoVivo"));
const NegociacoesAppTV = lazy(() => import("@/pages/NegociacoesAppTV"));
const PainelTV = lazy(() => import("@/pages/PainelTV"));

// Landing Page WhatsApp (Pública)
const LandingWhatsApp = lazy(() => import("@/pages/LandingWhatsApp"));

// Academy
const Academy = lazy(() => import("@/pages/Academy"));
const AcademyCurso = lazy(() => import("@/pages/AcademyCurso"));
const AcademyAula = lazy(() => import("@/pages/AcademyAula"));
const AcademyAdmin = lazy(() => import("@/pages/AcademyAdmin"));
const AcademyQuestionario = lazy(() => import("@/pages/AcademyQuestionario"));
const SecretariaIA = lazy(() => import("@/pages/SecretariaIA"));
const InstaIMOBSlides = lazy(() => import("@/pages/InstaIMOBSlides"));

// Compradores
const Compradores = lazy(() => import("@/pages/Compradores"));
const Contratos = lazy(() => import("@/pages/Contratos"));
const Pagamentos = lazy(() => import("@/pages/Pagamentos"));
const Reservas = lazy(() => import("@/pages/Reservas"));
const RedeCorretores = lazy(() => import("@/pages/RedeCorretores"));
const FunilCorretores = lazy(() => import("@/pages/FunilCorretores"));
const CorretorPerfil = lazy(() => import("@/pages/CorretorPerfil"));
const RepasseBancario = lazy(() => import("@/pages/RepasseBancario"));
const CompradorDetalhes = lazy(() => import("@/pages/CompradorDetalhes"));

// Portal do Cliente (público)
const PortalCliente = lazy(() => import("@/pages/PortalCliente"));

// Bairros
const Bairros = lazy(() => import("@/pages/Bairros"));

// Cidades
const Cidades = lazy(() => import("@/pages/Cidades"));

// Assinatura Digital (pública)
const AssinaturaDigital = lazy(() => import("@/pages/AssinaturaDigital"));
const PropostaPublica = lazy(() => import("@/pages/PropostaPublica"));

// Blog Admin
const AdminBlog = lazy(() => import("@/pages/admin/Blog"));
const AdminBlogEditor = lazy(() => import("@/pages/admin/BlogEditor"));

// Área do Proprietário de Imóvel
const ProprietarioLogin = lazy(() => import("@/pages/ProprietarioLogin"));
const ProprietarioDashboard = lazy(() => import("@/pages/ProprietarioDashboard"));
const CalculadoraFinanciamento = lazy(() => import("@/pages/CalculadoraFinanciamento"));

// Site público - DESATIVADO (apenas landing pages e mini sites mantidos)
// const SiteLayout = lazy(() => import("@/components/site/SiteLayout"));
// const SiteHome = lazy(() => import("@/pages/site/SiteHome"));
// const SiteImoveis = lazy(() => import("@/pages/site/SiteImoveis"));
// const SiteImovelDetalhes = lazy(() => import("@/pages/site/SiteImovelDetalhes"));
// const SiteEmpreendimentos = lazy(() => import("@/pages/site/SiteEmpreendimentos"));
// const SiteEmpreendimentoDetalhes = lazy(() => import("@/pages/site/SiteEmpreendimentoDetalhes"));
// const SiteConstrutoras = lazy(() => import("@/pages/site/SiteConstrutoras"));
// const SiteConstruturaDetalhes = lazy(() => import("@/pages/site/SiteConstruturaDetalhes"));
// const SiteContato = lazy(() => import("@/pages/site/SiteContato"));
// const SiteInstitucional = lazy(() => import("@/pages/site/SiteInstitucional"));
// const SiteBlog = lazy(() => import("@/pages/site/SiteBlog"));
// const SiteBlogPost = lazy(() => import("@/pages/site/SiteBlogPost"));
// const SiteFavoritos = lazy(() => import("@/pages/site/SiteFavoritos"));
// const SiteCadastro = lazy(() => import("@/pages/site/SiteCadastro"));
// const SiteAnunciarImovel = lazy(() => import("@/pages/site/SiteAnunciarImovel"));
// const SiteBairros = lazy(() => import("@/pages/site/SiteBairros"));
// const SiteBairroDetalhes = lazy(() => import("@/pages/site/SiteBairroDetalhes"));
const MiniSitePublic = lazy(() => import("@/pages/site/MiniSitePublic"));
const LandingPagePublic = lazy(() => import("@/pages/site/LandingPagePublic"));
// const PublicSite = lazy(() => import("@/pages/PublicSite"));

// Componente de rota protegida
const ProtectedRoute = ({ children, isAuth, redirectTo = "/login" }) => {
  return isAuth ? children : <Navigate to={redirectTo} />;
};

// Componente de rota lazy protegida
const LazyProtectedRoute = ({ component: Component, isAuth, redirectTo = "/login", ...props }) => {
  if (!isAuth) return <Navigate to={redirectTo} />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Component {...props} />
    </Suspense>
  );
};

// Função para obter a home page baseada na role do usuário
const getDefaultHomePage = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user) return "/login";
  if (user.role === "corretor") return "/funil";
  if (user.role === "engenheiro") return "/empreendimentos";
  return "/dashboard";
};

// Componente de proteção do Dashboard (apenas proprietário/admin)
const DashboardProtectedRoute = ({ children, isAuth }) => {
  if (!isAuth) return <Navigate to="/login" />;
  const user = JSON.parse(localStorage.getItem('user'));
  if (user?.role === "corretor") {
    return <Navigate to="/clientes" />;
  }
  if (user?.role === "engenheiro") {
    return <Navigate to="/empreendimentos" />;
  }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [isProprietarioAuth, setIsProprietarioAuth] = useState(() => !!localStorage.getItem('proprietario_token'));

  const handleLogin = () => setIsAuthenticated(true);
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    window.location.href = '/login';
  };
  const handleProprietarioLogin = () => setIsProprietarioAuth(true);
  const handleProprietarioLogout = () => {
    localStorage.removeItem('proprietario_token');
    localStorage.removeItem('proprietario');
    setIsProprietarioAuth(false);
    window.location.href = '/proprietario/login';
  };

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <Routes>
            {/* Raiz:
                - galpao.* → Landing Galpão
                - zenitebysalinet.com.br (ou começa com 'zenite.') → Landing Zênite
                - caso contrário → CRM */}
            <Route
              path="/"
              element={(() => {
                if (typeof window === 'undefined') return <Navigate to="/login" replace />;
                const host = window.location.hostname;
                if (host.startsWith('galpao.')) {
                  return <Suspense fallback={<PageLoader />}><GalpaoLanding /></Suspense>;
                }
                if (host === 'zenitebysalinet.com.br' || host === 'www.zenitebysalinet.com.br' || host.startsWith('zenite.')) {
                  return <Suspense fallback={<PageLoader />}><ZeniteLanding /></Suspense>;
                }
                if (host.startsWith('lp.')) {
                  return <Navigate to="/curriculo" replace />;
                }
                return <Navigate to="/login" replace />;
              })()}
            />
            <Route path="/salinet" element={<Navigate to="/login" replace />} />
            <Route path="/site" element={<Navigate to="/login" replace />} />
            <Route path="/sobre" element={<Navigate to="/login" replace />} />
            <Route path="/contato" element={<Navigate to="/login" replace />} />

            <Route path="/litoral" element={<Navigate to="/login" replace />} />
            
            {/* ========================================= */}
            {/* CONTEXTO CRM (/crm/*) - Solk Imóveis */}
            {/* ========================================= */}
            
            {/* Login CRM */}
            <Route 
              path="/login" 
              element={isAuthenticated ? <Navigate to={getDefaultHomePage()} /> : <Login onLogin={handleLogin} />} 
            />
            
            {/* Dashboard CRM (apenas proprietário/admin) */}
            <Route 
              path="/dashboard" 
              element={
                <DashboardProtectedRoute isAuth={isAuthenticated}>
                  <Dashboard onLogout={handleLogout} />
                </DashboardProtectedRoute>
              } 
            />
            
            {/* Rotas do CRM (lazy) */}
            <Route path="/funil" element={<LazyProtectedRoute component={FunilVendas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/oportunidades" element={<LazyProtectedRoute component={Oportunidades} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/oportunidades/:id" element={<LazyProtectedRoute component={OportunidadeDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/unidades" element={<LazyProtectedRoute component={Tabelas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/tabelas" element={<Navigate to="/unidades" replace />} />
            
            {/* Índices Econômicos */}
            <Route path="/indices/cub" element={<LazyProtectedRoute component={CubHistorico} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/indices/igpm" element={<LazyProtectedRoute component={Igpm} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/indices/incc" element={<LazyProtectedRoute component={Incc} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/indices/ipca" element={<LazyProtectedRoute component={Ipca} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/cub" element={<Navigate to="/indices/cub" replace />} />
            <Route path="/edificio/:edificio/unidade/:numero" element={<LazyProtectedRoute component={UnidadePerfil} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/propostas" element={<LazyProtectedRoute component={PropostasPage} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/proposta/:id" element={<LazyProtectedRoute component={PropostaPerfil} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/calculadora-financiamento" element={<LazyProtectedRoute component={CalculadoraFinanciamento} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/clients" element={<LazyProtectedRoute component={Clients} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/properties" element={<LazyProtectedRoute component={Properties} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/profile" element={<LazyProtectedRoute component={Profile} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/action-logs" element={<LazyProtectedRoute component={ActionLogs} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/ajuda" element={<LazyProtectedRoute component={Ajuda} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/captacoes" element={<LazyProtectedRoute component={Captacoes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/empreendimentos" element={<LazyProtectedRoute component={Empreendimentos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/empreendimentos/:id" element={<LazyProtectedRoute component={EmpreendimentoDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/clientes/:clientId" element={<LazyProtectedRoute component={ClienteDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/captacoes/:captacaoId" element={<LazyProtectedRoute component={CaptacaoDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/construtoras" element={<LazyProtectedRoute component={Construtoras} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/proprietarios" element={<LazyProtectedRoute component={Proprietarios} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/agendamentos" element={<LazyProtectedRoute component={Agendamentos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/disputas" element={<LazyProtectedRoute component={Disputas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/equipe" element={<LazyProtectedRoute component={Equipe} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/cargos-permissoes" element={<LazyProtectedRoute component={RBACManager} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/configuracoes" element={<LazyProtectedRoute component={Configuracoes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/mini-site" element={<LazyProtectedRoute component={MiniSite} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/landing-pages" element={<LazyProtectedRoute component={LandingPages} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/landing-pages/editor" element={<LazyProtectedRoute component={LandingPageEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/landing-pages/editor/:id" element={<LazyProtectedRoute component={LandingPageEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/visitantes" element={<LazyProtectedRoute component={Visitantes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/site-config" element={<LazyProtectedRoute component={SiteConfig} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/temas" element={<LazyProtectedRoute component={Temas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/dominios" element={<LazyProtectedRoute component={Dominios} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/negociacoes/ao-vivo" element={<LazyProtectedRoute component={NegociacoesAoVivo} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/negociacoes/app-tv" element={<LazyProtectedRoute component={NegociacoesAppTV} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/bairros" element={<LazyProtectedRoute component={Bairros} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/cidades" element={<LazyProtectedRoute component={Cidades} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/scrap-ia" element={<LazyProtectedRoute component={Importacao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/lembretes" element={<LazyProtectedRoute component={Lembretes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/email-marketing" element={<LazyProtectedRoute component={EmailMarketing} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/propostas" element={<LazyProtectedRoute component={Propostas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/compradores" element={<LazyProtectedRoute component={Compradores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/compradores/:id" element={<LazyProtectedRoute component={CompradorDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/contratos" element={<LazyProtectedRoute component={Contratos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/pagamentos" element={<LazyProtectedRoute component={Pagamentos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/repasse-bancario" element={<LazyProtectedRoute component={RepasseBancario} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/reservas" element={<LazyProtectedRoute component={Reservas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/rede-corretores" element={<LazyProtectedRoute component={RedeCorretores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/rede-corretores/funil" element={<LazyProtectedRoute component={FunilCorretores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/rede-corretores/:tab" element={<LazyProtectedRoute component={RedeCorretores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/rede-corretores/corretor/:id" element={<LazyProtectedRoute component={CorretorPerfil} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/atualizacao" element={<LazyProtectedRoute component={Atualizacao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Bolsão de Leads - CRM */}
            <Route path="/bolsao" element={<LazyProtectedRoute component={Bolsao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/bolsao/campanhas" element={<LazyProtectedRoute component={CampanhasBolsao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/bolsao/campanhas/:campanhaId" element={<LazyProtectedRoute component={CampanhaDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/bolsao/minha-campanha" element={<LazyProtectedRoute component={MinhaCampanha} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Pixel Pro - CRM */}
            <Route path="/pixel-pro" element={<LazyProtectedRoute component={PixelProDashboard} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/pixel-pro/events" element={<LazyProtectedRoute component={PixelProEvents} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/pixel-pro/rules" element={<LazyProtectedRoute component={PixelProRules} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/pixel-pro/whatsapp" element={<LazyProtectedRoute component={PixelProWhatsApp} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/pixel-pro/meta-capi" element={<LazyProtectedRoute component={MetaCAPIConfig} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* InstaIMOB ADS - Gestão de Anúncios */}
            <Route path="/instaimob-ads" element={<LazyProtectedRoute component={InstaIMOBAdsDashboard} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/instaimob-ads/campanhas" element={<LazyProtectedRoute component={CampanhasWhatsApp} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            {/* Aliases legados (bookmarks antigos) → redirecionam ao novo path */}
            <Route path="/solk-ads" element={<Navigate to="/instaimob-ads" replace />} />
            <Route path="/solk-ads/campanhas" element={<Navigate to="/instaimob-ads/campanhas" replace />} />

            {/* QR Codes Inteligentes */}
            <Route path="/marketing/qrcodes" element={<LazyProtectedRoute component={QRCodesListPage} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/marketing/qrcodes/:codigo" element={<LazyProtectedRoute component={QRCodeProfilePage} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Academy - CRM */}
            <Route path="/academy" element={<LazyProtectedRoute component={Academy} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/academy/curso/:cursoId" element={<LazyProtectedRoute component={AcademyCurso} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/academy/aula/:aulaId" element={<LazyProtectedRoute component={AcademyAula} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/academy/questionario/:questionarioId" element={<LazyProtectedRoute component={AcademyQuestionario} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/academy/admin" element={<LazyProtectedRoute component={AcademyAdmin} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Secretária IA - CRM */}
            <Route path="/secretaria" element={<LazyProtectedRoute component={SecretariaIA} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* InstaIMOB Slides - Ferramenta */}
            <Route path="/instaimob-slides" element={<LazyProtectedRoute component={InstaIMOBSlides} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Blog Admin - CRM */}
            <Route path="/admin/blog" element={<LazyProtectedRoute component={AdminBlog} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/admin/blog/novo" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/admin/blog/:id" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Blog Admin - Rotas alternativas sem /crm */}
            <Route path="/admin/blog" element={<LazyProtectedRoute component={AdminBlog} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/admin/blog/novo" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/admin/blog/:id" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Proprietário de Imóvel - CRM */}
            <Route 
              path="/proprietario/login" 
              element={isProprietarioAuth ? <Navigate to="/proprietario/dashboard" /> : (
                <Suspense fallback={<PageLoader />}>
                  <ProprietarioLogin onLogin={handleProprietarioLogin} />
                </Suspense>
              )} 
            />
            <Route 
              path="/proprietario/dashboard" 
              element={
                <LazyProtectedRoute 
                  component={ProprietarioDashboard} 
                  isAuth={isProprietarioAuth} 
                  redirectTo="/proprietario/login"
                  onLogout={handleProprietarioLogout} 
                />
              } 
            />
            
            {/* Redirects de rotas antigas para CRM */}
            <Route path="/imoveis" element={<Navigate to="/login" replace />} />
            <Route path="/imovel/*" element={<Navigate to="/login" replace />} />
            <Route path="/construtoras" element={<Navigate to="/login" replace />} />
            <Route path="/construtora/*" element={<Navigate to="/login" replace />} />
            <Route path="/bairros" element={<Navigate to="/login" replace />} />
            <Route path="/bairro/*" element={<Navigate to="/login" replace />} />
            <Route path="/institucional" element={<Navigate to="/login" replace />} />
            <Route path="/blog" element={<Navigate to="/login" replace />} />
            <Route path="/blog/*" element={<Navigate to="/login" replace />} />
            <Route path="/favoritos" element={<Navigate to="/login" replace />} />
            <Route path="/cadastro" element={<Navigate to="/login" replace />} />
            <Route path="/anunciar-imovel" element={<Navigate to="/login" replace />} />
            <Route path="/obrigado" element={<Navigate to="/login" replace />} />
            <Route path="/trabalhe-conosco" element={<Navigate to="/login" replace />} />
            
            {/* ========================================= */}
            {/* ROTAS PÚBLICAS (sem prefixo de contexto) */}
            {/* ========================================= */}
            
            {/* Mini Site e Landing Pages Públicas */}
            <Route path="/s/:slug" element={<Suspense fallback={<PageLoader />}><MiniSitePublic /></Suspense>} />
            <Route path="/lp/:slug" element={<Suspense fallback={<PageLoader />}><LandingPagePublic /></Suspense>} />

            {/* Landing Pages Salinet — usadas em galpao.salinetempreendimentos.com.br */}
            <Route path="/galpao" element={<Suspense fallback={<PageLoader />}><GalpaoLanding /></Suspense>} />
            <Route path="/galpao/obrigado" element={<Suspense fallback={<PageLoader />}><GalpaoObrigado /></Suspense>} />
            <Route path="/obrigado" element={<Suspense fallback={<PageLoader />}><GalpaoObrigado /></Suspense>} />

            {/* Landing Page Zênite by Salinet — produção: zenitebysalinet.com.br */}
            <Route path="/zenite" element={<Suspense fallback={<PageLoader />}><ZeniteLanding /></Suspense>} />

            {/* Landing de Carreiras — produção: lp.salinetempreendimentos.com.br/curriculo */}
            <Route path="/curriculo" element={<Suspense fallback={<PageLoader />}><CurriculoLanding /></Suspense>} />
            <Route path="/curriculo/obrigado" element={<Suspense fallback={<PageLoader />}><CurriculoObrigado /></Suspense>} />
            <Route path="/carreiras" element={<Navigate to="/curriculo" replace />} />
            <Route path="/vagas" element={<Navigate to="/curriculo" replace />} />
            
            {/* Painel TV - Página PÚBLICA */}
            <Route path="/painel-tv" element={<Suspense fallback={<PageLoader />}><PainelTV /></Suspense>} />
            
            {/* Landing Page WhatsApp - Pública */}
            <Route path="/l/:tenantSlug/:slug" element={<Suspense fallback={<PageLoader />}><LandingWhatsApp /></Suspense>} />
            
            {/* Assinatura Digital - Pública */}
            <Route path="/assinatura/:propostaId" element={<Suspense fallback={<PageLoader />}><AssinaturaDigital /></Suspense>} />
            
            {/* Visualização de Proposta - Pública */}
            <Route path="/proposta/:propostaId" element={<Suspense fallback={<PageLoader />}><PropostaPublica /></Suspense>} />
            
            {/* Portal do Cliente - Pública */}
            <Route path="/portal-cliente/:token" element={<Suspense fallback={<PageLoader />}><PortalCliente /></Suspense>} />
            
            {/* Redirect padrão - CRM */}
            <Route path="/" element={<Navigate to={isAuthenticated ? getDefaultHomePage() : "/login"} />} />
            
            {/* Redirect /login para /crm/login */}
            <Route path="/login" element={<Navigate to="/login" replace />} />
            
            {/* Catch-all: redireciona rotas não encontradas */}
            <Route path="*" element={<Navigate to={isAuthenticated ? getDefaultHomePage() : "/login"} replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
